import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Rocket, Plus, Target, DollarSign, Clock, AlertTriangle, CheckCircle2,
  Play, Pause, BarChart3, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getAuthToken } from '../utils/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ExecutionHub() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newPlan, setNewPlan] = useState({
    invention_id: '',
    project_name: '',
    description: '',
    total_budget_estimate: ''
  });

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/execution/plans`,
        {
          headers: { Authorization: `Bearer ${getAuthToken()}` }
        }
      );
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Failed to load execution plans:', error);
      toast.error('Failed to load execution plans');
    } finally {
      setLoading(false);
    }
  };

  const createPlan = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/api/execution/plans`,
        {
          ...newPlan,
          total_budget_estimate: parseFloat(newPlan.total_budget_estimate)
        },
        {
          headers: { Authorization: `Bearer ${getAuthToken()}` }
        }
      );

      toast.success('Execution plan created successfully!');
      setShowCreateDialog(false);
      setNewPlan({ invention_id: '', project_name: '', description: '', total_budget_estimate: '' });
      loadPlans();
    } catch (error) {
      console.error('Failed to create plan:', error);
      toast.error(error.response?.data?.detail || 'Failed to create execution plan');
    }
  };

  const activatePlan = async (planId, projectName) => {
    const confirm = window.confirm(
      `Activate "${projectName}" as PRIMARY project?\n\nThis will make it the active execution focus. Only ONE primary project allowed.`
    );

    if (!confirm) return;

    try {
      const response = await axios.post(
        `${API_URL}/api/execution/plans/${planId}/activate`,
        { confirm: true },
        {
          headers: { Authorization: `Bearer ${getAuthToken()}` }
        }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        loadPlans();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Failed to activate plan:', error);
      toast.error(error.response?.data?.detail || 'Failed to activate plan');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      planning: 'bg-gray-500',
      active: 'bg-green-500',
      paused: 'bg-yellow-500',
      completed: 'bg-blue-500',
      cancelled: 'bg-red-500',
      archived: 'bg-gray-400'
    };
    return colors[status] || 'bg-gray-500';
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent mb-4"></div>
            <p className="text-muted-foreground">Loading execution plans...</p>
          </div>
        </div>
      </AppShell>
    );
  }

  const primaryProject = plans.find(p => p.is_primary);
  const secondaryProjects = plans.filter(p => !p.is_primary);

  return (
    <AppShell>
      <div className="container mx-auto p-6 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold tracking-tight mb-2 flex items-center gap-3">
                <Rocket className="h-10 w-10 text-primary" />
                Execution Orchestration
              </h1>
              <p className="text-muted-foreground text-lg">
                Transform planning into execution • Coordinate vendors, milestones, and capital
              </p>
            </div>
            <Button onClick={() => setShowCreateDialog(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              New Execution Plan
            </Button>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Projects</p>
                    <p className="text-3xl font-bold">{plans.length}</p>
                  </div>
                  <Target className="h-8 w-8 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Primary Project</p>
                    <p className="text-3xl font-bold">{primaryProject ? '1' : '0'}</p>
                  </div>
                  <Zap className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Planning Stage</p>
                    <p className="text-3xl font-bold">
                      {plans.filter(p => p.status === 'planning').length}
                    </p>
                  </div>
                  <Clock className="h-8 w-8 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Primary Project */}
        {primaryProject && (
          <div className="mb-8">
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
              <Zap className="h-6 w-6 text-green-500" />
              Primary Active Project
            </h2>
            <Card className="border-green-500/50 bg-green-500/5">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-2xl mb-2">{primaryProject.project_name}</CardTitle>
                    <CardDescription>{primaryProject.description}</CardDescription>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Badge variant="default" className="bg-green-500">PRIMARY</Badge>
                    <Badge variant="outline" className="capitalize">{primaryProject.status}</Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Budget</p>
                    <p className="text-lg font-semibold">
                      ${primaryProject.total_budget_estimate.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Spent</p>
                    <p className="text-lg font-semibold">
                      ${primaryProject.total_budget_spent?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Milestones</p>
                    <p className="text-lg font-semibold">{primaryProject.milestones?.length || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Vendors</p>
                    <p className="text-lg font-semibold">{primaryProject.vendors?.length || 0}</p>
                  </div>
                </div>

                <Button
                  onClick={() => navigate(`/execution/project/${primaryProject.plan_id}`)}
                  className="w-full gap-2"
                >
                  <BarChart3 className="h-4 w-4" />
                  Open Command Dashboard
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Execution Discipline Rule */}
        {!primaryProject && plans.length > 0 && (
          <Alert className="mb-8 border-yellow-500/50 bg-yellow-500/10">
            <AlertTriangle className="h-4 w-4 text-yellow-500" />
            <AlertDescription>
              <strong>Execution Discipline:</strong> No primary project active. Activate one project to begin execution.
            </AlertDescription>
          </Alert>
        )}

        {/* Secondary Projects */}
        {secondaryProjects.length > 0 && (
          <div>
            <h2 className="text-2xl font-semibold mb-4">
              Planning & Secondary Projects
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {secondaryProjects.map((plan) => (
                <Card key={plan.plan_id} className="hover:border-primary/50 transition-colors">
                  <CardHeader>
                    <div className="flex items-start justify-between mb-2">
                      <CardTitle>{plan.project_name}</CardTitle>
                      <Badge variant="outline" className="capitalize">
                        {plan.status}
                      </Badge>
                    </div>
                    <CardDescription className="line-clamp-2">{plan.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Budget</p>
                        <p className="font-semibold">
                          ${plan.total_budget_estimate.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Milestones</p>
                        <p className="font-semibold">{plan.milestones?.length || 0}</p>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/execution/project/${plan.plan_id}`)}
                        className="flex-1"
                      >
                        View Details
                      </Button>
                      {plan.status === 'planning' && (
                        <Button
                          size="sm"
                          onClick={() => activatePlan(plan.plan_id, plan.project_name)}
                          className="flex-1 gap-2"
                        >
                          <Play className="h-3 w-3" />
                          Activate
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {plans.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Rocket className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-2xl font-semibold mb-2">No Execution Plans Yet</h3>
              <p className="text-muted-foreground text-center max-w-md mb-6">
                Create your first execution plan to transform ideas into reality with structured
                milestones, vendor coordination, and capital management.
              </p>
              <Button onClick={() => setShowCreateDialog(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                Create Execution Plan
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Create Plan Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Execution Plan</DialogTitle>
              <DialogDescription>
                Set up a new project with automatic 7-stage milestone pipeline
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="project_name">Project Name *</Label>
                <Input
                  id="project_name"
                  value={newPlan.project_name}
                  onChange={(e) => setNewPlan({ ...newPlan, project_name: e.target.value })}
                  placeholder="e.g., On Point Jar Production"
                />
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={newPlan.description}
                  onChange={(e) => setNewPlan({ ...newPlan, description: e.target.value })}
                  placeholder="Brief description of the project and goals..."
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="budget">Total Budget Estimate * ($)</Label>
                <Input
                  id="budget"
                  type="number"
                  value={newPlan.total_budget_estimate}
                  onChange={(e) => setNewPlan({ ...newPlan, total_budget_estimate: e.target.value })}
                  placeholder="50000"
                />
              </div>

              <div>
                <Label htmlFor="invention_id">Invention ID (optional)</Label>
                <Input
                  id="invention_id"
                  value={newPlan.invention_id}
                  onChange={(e) => setNewPlan({ ...newPlan, invention_id: e.target.value })}
                  placeholder="Link to existing invention if applicable"
                />
              </div>

              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>
                  This will create an execution plan with 7 automatic milestones:
                  Concept Lock → Engineering Design → Prototype Build → Validation Testing →
                  Certification → Pilot Manufacturing → Scale Production
                </AlertDescription>
              </Alert>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={createPlan}
                disabled={!newPlan.project_name || !newPlan.description || !newPlan.total_budget_estimate}
              >
                Create Plan
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
}
